import React, { useState, useRef } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Typography,
    Box,
    Grid,
    Divider,
    Button,
    Radio,
    RadioGroup,
    FormControlLabel,
    CircularProgress,
    IconButton
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

function formatDate(dateString) {
    if (!dateString) return '';
    const parsedDate = new Date(dateString);
    if (isNaN(parsedDate)) return '';
    return parsedDate.toLocaleDateString();
}

function isLicenseExpired(license) {
    if (!license?.validUntil) return true;
    const expiry = new Date(license.validUntil);
    return expiry < new Date();
}

export default function ShowLicenseDialog({
    license,
    token,
    fetchLicense,
    open = true,
    onClose,
    onSaveOwnerDetails,
    onCreateCheckout,
    onCancelSubscription,
    onUpdateSubscription,
    onOpenCustomerPortal
}) {
    const [ownerDetails, setOwnerDetails] = useState(license?.ownerDetails ?? {
        company: '',
        vatId: '',
        name: '',
        firstName: '',
        email: '',
        phoneNumber: '',
        street: '',
        houseNameOrNumber: '',
        city: '',
        postCode: '',
        country: ''
    });

    const [restaurantId, setRestaurantId] = useState(license?.apiKeys?.restaurantId || '');
    const [saving, setSaving] = useState(false);
    const [refreshing, setRefreshing] = useState(false);
    const [cancelRefreshing, setCancelRefreshing] = useState(false);
    const [openingPortal, setOpeningPortal] = useState(false);
    const [showOrderDialog, setShowOrderDialog] = useState(false);

    const dragRef = useRef(null);
    const pos = useRef({ x: 0, y: 0 });
    const offset = useRef({ x: 0, y: 0 });
    const dragging = useRef(false);

    const startDrag = (e) => {
        dragging.current = true;
        offset.current = {
            x: e.clientX - pos.current.x,
            y: e.clientY - pos.current.y
        };
        document.addEventListener('mousemove', onDrag);
        document.addEventListener('mouseup', stopDrag);
    };

    const onDrag = (e) => {
        if (!dragging.current) return;
        pos.current = {
            x: e.clientX - offset.current.x,
            y: e.clientY - offset.current.y
        };
        if (dragRef.current) {
            dragRef.current.style.transform = `translate(${pos.current.x}px, ${pos.current.y}px)`;
        }
    };

    const stopDrag = () => {
        dragging.current = false;
        document.removeEventListener('mousemove', onDrag);
        document.removeEventListener('mouseup', stopDrag);
    };

    const mapTypeToRadio = (type) => {
        if (type === 'Yearly') return 'Fixed';
        if (type === 'Monthly') return 'Subscription';
        return 'Fixed';
    };

    const [selectedPlan, setSelectedPlan] = useState(
        license?.subscriptionType ? mapTypeToRadio(license.subscriptionType) : 'Fixed'
    );

    const handleOwnerDetailChange = (field) => (event) => {
        setOwnerDetails({
            ...ownerDetails,
            [field]: event.target.value
        });
    };

    const handleSave = async () => {
        setSaving(true);
        if (onSaveOwnerDetails) {
            if (ownerDetails.email === '') {
                alert('Bitte E-Mail-Adresse eintragen!');
                setSaving(false);
                return;
            }
            await onSaveOwnerDetails(ownerDetails, restaurantId);
        }
        setSaving(false);
    };

    const pollForUpdate = async (predicate, isCancel = false, timeoutMs = 20000, intervalMs = 3000) => {
        if (isCancel) setCancelRefreshing(true);
        else setRefreshing(true);

        const start = Date.now();
        while (Date.now() - start < timeoutMs) {
            const updated = await fetchLicense(token);
            if (updated && predicate(updated)) {
                if (isCancel) setCancelRefreshing(false);
                else setRefreshing(false);
                return updated;
            }
            await new Promise((res) => setTimeout(res, intervalMs));
        }

        if (isCancel) setCancelRefreshing(false);
        else setRefreshing(false);
        return null;
    };

    const handleUpdateSubscription = async () => {
        await onUpdateSubscription(license.tillId, license.product, selectedPlan);
        await pollForUpdate((license) => license.subscriptionType === mapLicenseTypeToSubscriptionType(selectedPlan));
    };

    const handleCancelSubscription = async () => {
        await onCancelSubscription(license.tillId, license.product);
        await pollForUpdate((license) => !license.stripeSubscriptionId || license.stripeSubscriptionId === '', true);
        window.location.reload();
    };

    const handleOpenCustomerPortal = async () => {
        setOpeningPortal(true);
        try {
            await onOpenCustomerPortal(license.id, token);
        } finally {
            setOpeningPortal(false);
        }
    };

    if (!license) return null;
    const expired = isLicenseExpired(license);

    const mapLicenseType = (type) => {
        if (type === 'Fixed') return 'Einmalig';
        if (type === 'Subscription') return 'Abonnement';
        return type || '—';
    };

    const mapSubscriptionType = (type) => {
        if (type === 'Yearly') return 'Jahresabo';
        if (type === 'Monthly') return 'Monatsabo';
        return '—';
    };

    const mapLicenseTypeToSubscriptionType = (type) => {
        if (type === 'Fixed') return 'Yearly';
        if (type === 'Subscription') return 'Monthly';
        return type || '—';
    };

    // --- NEW LOGIC ---
    const forceShowOverlay = !license.isActive || expired;  // always show if license invalid
    const overlayVisible = forceShowOverlay || showOrderDialog;

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>Lizenzdetails</DialogTitle>

            <DialogContent
                dividers
                sx={{
                    position: 'relative',
                    height: '80vh',
                    overflowY: 'auto',
                    pt: 1
                }}
            >
                {/* --- ORDER OVERLAY --- */}
                {overlayVisible && (
                    <Box
                        ref={dragRef}
                        sx={{
                            position: 'absolute',
                            top: '10%',
                            left: '50%',
                            transform: 'translateX(-50%)',
                            backgroundColor: 'white',
                            borderRadius: 2,
                            p: 3,
                            boxShadow: 6,
                            zIndex: 5,
                            width: '80%',
                            maxWidth: 500,
                            cursor: 'move',
                            userSelect: 'none'
                        }}
                        onMouseDown={startDrag}
                    >
                        <Box
                            sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                mb: 2
                            }}
                        >
                            <Typography variant="subtitle1">Abo-Option wählen</Typography>
                            {/* --- Close button now works --- */}
                            {!forceShowOverlay && (
                                <IconButton size="small" onClick={() => setShowOrderDialog(false)}>
                                    <CloseIcon />
                                </IconButton>
                            )}
                        </Box>

                        <RadioGroup
                            row
                            value={selectedPlan}
                            onChange={(e) => setSelectedPlan(e.target.value)}
                        >
                            <FormControlLabel
                                value="Fixed"
                                control={<Radio />}
                                label="Jahresabo (16,99€/Monat)"
                            />
                            <FormControlLabel
                                value="Subscription"
                                control={<Radio />}
                                label="Monatsabo (19,99€/Monat)"
                            />
                        </RadioGroup>

                        <Box display="flex" gap={2} mt={2}>
                            {!license.isActive || expired ? (
                                <Button
                                    variant="contained"
                                    color="primary"
                                    onClick={() => {
                                        if (ownerDetails.email === '') {
                                            alert('Bitte E-Mail-Adresse eintragen!');
                                            return;
                                        }
                                        onCreateCheckout(license.tillId, selectedPlan);
                                    }}
                                >
                                    Lizenz erwerben
                                </Button>
                            ) : (
                                <>
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        onClick={handleUpdateSubscription}
                                        disabled={refreshing}
                                    >
                                        {refreshing ? <CircularProgress size={20} /> : "Abo ändern"}
                                    </Button>
                                    <Button
                                        variant="outlined"
                                        color="error"
                                        onClick={handleCancelSubscription}
                                    >
                                        {cancelRefreshing ? <CircularProgress size={20} /> : "Abo beenden"}
                                    </Button>
                                    <Button
                                        variant="outlined"
                                        color="secondary"
                                        onClick={handleOpenCustomerPortal}
                                        disabled={openingPortal}
                                    >
                                        {openingPortal ? <CircularProgress size={20} /> : "Zahlungsdetails anzeigen"}
                                    </Button>
                                </>
                            )}
                        </Box>
                    </Box>
                )}

                {/* --- LICENSE DETAILS --- */}
                <Grid container spacing={2} sx={{ opacity: overlayVisible ? 0.3 : 1 }}>
                    <Grid item xs={12} sm={6}>
                        <TextField label="Lizenznehmer" value={license.owner || ''} fullWidth margin="normal" InputProps={{ readOnly: true }} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField label="Kassen-Id" value={license.tillId || ''} fullWidth margin="normal" InputProps={{ readOnly: true }} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField label="Produkt" value={license.product || ''} fullWidth margin="normal" InputProps={{ readOnly: true }} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField label="Lizenztyp" value={mapLicenseType(license.licenseType)} fullWidth margin="normal" InputProps={{ readOnly: true }} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField label="Abo-Typ" value={mapSubscriptionType(license.subscriptionType)} fullWidth margin="normal" InputProps={{ readOnly: true }} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField label="Gültig bis" value={formatDate(license.validUntil)} fullWidth margin="normal" InputProps={{ readOnly: true }} />
                    </Grid>
                </Grid>

                {!forceShowOverlay && (
                    <>
                        <Divider sx={{ my: 2 }} />
                        <Box textAlign="center" mb={2}>
                            <Button variant="contained" color="primary" onClick={() => setShowOrderDialog(true)}>
                                Abonnement bearbeiten
                            </Button>
                        </Box>
                        <Divider sx={{ my: 2 }} />
                    </>
                )}

                {/* --- OWNER DETAILS SECTION --- */}
                <Typography variant="subtitle1" sx={{ mt: 2, mb: 1 }}>
                    Lizenznehmer Details
                </Typography>
                <Grid container spacing={2} sx={{ pb: 3 }}>
                    <Grid item xs={12} sm={6}>
                        <TextField label="Firma" fullWidth margin="normal" value={ownerDetails.company} onChange={handleOwnerDetailChange('company')} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField label="USt-IdNr." fullWidth margin="normal" value={ownerDetails.vatId} onChange={handleOwnerDetailChange('vatId')} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField label="Name" fullWidth margin="normal" value={ownerDetails.name} onChange={handleOwnerDetailChange('name')} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField label="Vorname" fullWidth margin="normal" value={ownerDetails.firstName} onChange={handleOwnerDetailChange('firstName')} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField label="E-Mail" fullWidth margin="normal" value={ownerDetails.email} onChange={handleOwnerDetailChange('email')} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField label="Telefonnummer" fullWidth margin="normal" value={ownerDetails.phoneNumber} onChange={handleOwnerDetailChange('phoneNumber')} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField label="Straße" fullWidth margin="normal" value={ownerDetails.street} onChange={handleOwnerDetailChange('street')} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField label="Hausnummer" fullWidth margin="normal" value={ownerDetails.houseNameOrNumber} onChange={handleOwnerDetailChange('houseNameOrNumber')} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField label="Stadt" fullWidth margin="normal" value={ownerDetails.city} onChange={handleOwnerDetailChange('city')} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField label="PLZ" fullWidth margin="normal" value={ownerDetails.postCode} onChange={handleOwnerDetailChange('postCode')} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField label="Land" fullWidth margin="normal" value={ownerDetails.country} onChange={handleOwnerDetailChange('country')} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            label="Restaurant-Id"
                            fullWidth
                            margin="normal"
                            value={restaurantId}
                            onChange={(e) => setRestaurantId(e.target.value)}
                            InputProps={{ readOnly: !!restaurantId }}
                        />
                    </Grid>
                </Grid>
            </DialogContent>

            <DialogActions sx={{ justifyContent: 'space-between' }}>
                {(!license.isActive || expired) && (
                    <Typography color="error" sx={{ ml: 2 }}>
                        {expired ? 'Status: Abgelaufen' : 'Status: Deaktiviert'}
                    </Typography>
                )}
                <Button variant="contained" onClick={handleSave} disabled={saving}>
                    {saving ? "Speichern..." : "Speichern"}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
